/**
 * useChat - Hook for managing chat state with streaming and file support
 *
 * Handles:
 * - Message state management (add, update, clear)
 * - Streaming responses from API via SSE
 * - File attachments upload before sending (HOME-10)
 * - Video transcription requests (HOME-11)
 * - Error handling with retry support
 *
 * The hook uses Server-Sent Events (SSE) for streaming responses,
 * which provides token-by-token updates like ChatGPT.
 */

import { useState, useCallback, useRef } from 'react';
import type { Message, MessageAttachment } from '../components/chat/ChatMessage';
import { useFileUpload } from './useFileUpload';

// ============================================================================
// TYPES
// ============================================================================

interface UseChatOptions {
  /** Board ID for scoping chat to a specific board */
  boardId: string;
  /** Error callback for handling failures */
  onError?: (error: Error) => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Capitalize the first letter of a string.
 * Used for agent name display.
 */
function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for chat functionality with streaming support.
 *
 * @param options - Board ID and optional error callback
 * @returns Chat state and utilities
 *
 * @example
 * ```tsx
 * const { messages, sendMessage, isLoading } = useChat({ boardId });
 *
 * // Send a message
 * await sendMessage('Hello @Researcher, help me analyze this', ['researcher'], []);
 *
 * // Messages update in real-time as streaming response arrives
 * ```
 */
export function useChat({ boardId, onError }: UseChatOptions) {
  // Message history state
  const [messages, setMessages] = useState<Message[]>([]);

  // Loading state (true while waiting for or streaming response)
  const [isLoading, setIsLoading] = useState(false);

  // Error message state
  const [error, setError] = useState<string | null>(null);

  // Abort controller for cancelling in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // File upload utilities
  const { uploadFiles, isUploading } = useFileUpload({ boardId, onError });

  // -------------------------------------------------------------------------
  // SEND MESSAGE
  // -------------------------------------------------------------------------

  /**
   * Send a message and stream the response.
   *
   * @param content - Message text content
   * @param taggedAgents - Array of agent IDs that were @ mentioned
   * @param files - Array of files to attach (will be uploaded first)
   */
  const sendMessage = useCallback(
    async (
      content: string,
      taggedAgents: string[],
      files: File[] = []
    ) => {
      // Upload files first if any (HOME-10)
      let attachments: MessageAttachment[] = [];
      if (files.length > 0) {
        attachments = await uploadFiles(files);
      }

      // Create user message
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: 'user',
        content,
        createdAt: new Date(),
        attachments: attachments.length > 0 ? attachments : undefined,
      };

      // Add user message to state
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      // Create placeholder for assistant response
      const assistantId = `assistant-${Date.now()}`;
      const assistantMessage: Message = {
        id: assistantId,
        role: 'assistant',
        content: '',
        agentId: taggedAgents[0], // Primary agent
        agentName: taggedAgents[0] ? capitalizeFirst(taggedAgents[0]) : undefined,
        createdAt: new Date(),
        isStreaming: true,
      };

      setMessages((prev) => [...prev, assistantMessage]);

      try {
        // Create abort controller for cancellation
        abortControllerRef.current = new AbortController();

        // Send request to chat API
        const response = await fetch(`/api/boards/${boardId}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            // Send full message history for context
            messages: [...messages, userMessage].map((m) => ({
              role: m.role,
              content: m.content,
              attachments: m.attachments,
            })),
            taggedAgents,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`Chat request failed: ${response.statusText}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        // Read streaming response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedContent = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          // Decode chunk and parse SSE format
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            // SSE format: "data: {json}"
            if (line.startsWith('data: ')) {
              const data = line.slice(6);

              // Stream complete signal
              if (data === '[DONE]') {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId ? { ...m, isStreaming: false } : m
                  )
                );
                continue;
              }

              try {
                const parsed = JSON.parse(data);

                // Handle different event types
                if (parsed.type === 'text') {
                  // Accumulate text content
                  accumulatedContent += parsed.content;
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId
                        ? { ...m, content: accumulatedContent }
                        : m
                    )
                  );
                } else if (parsed.type === 'thinking') {
                  // Accumulate thinking content
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId
                        ? { ...m, thinking: (m.thinking || '') + parsed.content }
                        : m
                    )
                  );
                } else if (parsed.type === 'error') {
                  throw new Error(parsed.content);
                }
              } catch {
                // Ignore parse errors for incomplete JSON chunks
                // This can happen when a chunk is split mid-JSON
              }
            }
          }
        }
      } catch (err) {
        // Check if request was aborted
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        onError?.(err instanceof Error ? err : new Error(errorMessage));

        // Update assistant message with error content
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: `Error: ${errorMessage}`, isStreaming: false }
              : m
          )
        );
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    },
    [boardId, messages, onError, uploadFiles]
  );

  // -------------------------------------------------------------------------
  // TRANSCRIPTION (HOME-11)
  // -------------------------------------------------------------------------

  /**
   * Request transcription for a video/audio attachment.
   *
   * Uses Workers AI Whisper model to transcribe audio/video files.
   * Implements HOME-11: Video transcription via Workers AI.
   *
   * @param attachmentId - The attachment ID (R2 key) to transcribe
   */
  const transcribe = useCallback(
    async (attachmentId: string) => {
      try {
        // Call transcription endpoint with attachment ID in URL
        const response = await fetch(`/api/attachments/${attachmentId}/transcribe`, {
          method: 'POST',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Transcription failed');
        }

        const { transcription } = await response.json();

        // Update the attachment with transcription text
        setMessages((prev) =>
          prev.map((m) => ({
            ...m,
            attachments: m.attachments?.map((a) =>
              a.id === attachmentId ? { ...a, transcription } : a
            ),
          }))
        );
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Transcription failed');
        onError?.(error);
      }
    },
    [onError]
  );

  // -------------------------------------------------------------------------
  // UTILITIES
  // -------------------------------------------------------------------------

  /**
   * Stop the current streaming response.
   */
  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  /**
   * Clear all chat history.
   */
  const clear = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  /**
   * Retry the last failed message.
   * Removes the failed assistant message and resends the user message.
   */
  const retry = useCallback(() => {
    if (messages.length < 2) return;

    // Find the last user message
    const lastUserMessage = messages[messages.length - 2];
    if (lastUserMessage.role !== 'user') return;

    // Remove the failed assistant message
    setMessages((prev) => prev.slice(0, -1));

    // Resend (simplified - doesn't include original agents/files)
    sendMessage(lastUserMessage.content, [], []);
  }, [messages, sendMessage]);

  return {
    /** Array of all chat messages */
    messages,
    /** True while waiting for or streaming response */
    isLoading,
    /** True while uploading files */
    isUploading,
    /** Error message if last request failed */
    error,
    /** Send a new message */
    sendMessage,
    /** Request transcription for an attachment */
    transcribe,
    /** Stop current streaming response */
    stop,
    /** Clear all chat history */
    clear,
    /** Retry last failed message */
    retry,
  };
}
